/**
 * AccessLogger - Client-side logger for tracking server requests
 * Logs routes and static assets (JS, CSS, images) from this server
 * Excludes external API calls
 * Matches server log format
 * 
 * Integrates with AccessLogger.php backend
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Configuration
// Use root-relative path that works in both dev and production
// In production, AccessLogger.php should be in the web root or accessible via this path
const isLocalhost = window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname === '::1';

const LOG_ENDPOINT = process.env.REACT_APP_LOG_ENDPOINT || 
                    (isLocalhost 
                        ? 'http://localhost/slp_member_frontend/public/AccessLogger.php'
                        : `${window.location.origin}/AccessLogger.php`);
const ENABLE_LOGGING = process.env.REACT_APP_ENABLE_ACCESS_LOGGING !== 'FALSE'; // Default: enabled

/**
 * Check if URL should be excluded from logging
 * Only excludes external API calls and the logger endpoint itself
 * Allows: routes, static files (CSS, JS, images) served from this server
 * 
 * @param {string} url - URL to check
 * @returns {boolean} True if URL should be excluded
 */
function shouldExcludeUrl(url) {
    if (!url) return true;
    
    // Exclude AccessLogger.php to prevent infinite loop
    if (url.includes('AccessLogger.php')) {
        return true;
    }
    
    // Exclude webpack HMR (hot module replacement) files
    if (url.includes('.hot-update.js') || url.includes('.hot-update.json')) {
        return true;
    }
    
    // Get current server origin
    const currentOrigin = window.location.origin;
    
    try {
        const urlObj = new URL(url);
        
        // Exclude common third-party domains (analytics, CDNs, etc.)
        const excludedDomains = [
            'google-analytics.com',
            'googletagmanager.com',
            'facebook.com',
            'doubleclick.net',
            'analytics.google.com',
            'googleadservices.com'
        ];
        
        if (excludedDomains.some(domain => urlObj.hostname.includes(domain))) {
            return true;
        }
        
        // Exclude all external origins (not same-origin)
        if (urlObj.origin !== currentOrigin) {
            return true;
        }
        
        // Allow only same-origin requests
        return false;
    } catch (e) {
        // If URL parsing fails, it's a relative URL (same origin), allow it
        return false;
    }
}

/**
 * Send log entry to backend
 * Uses fetch for reliability, or optionally sendBeacon for page unload scenarios
 * 
 * @param {Object} data Log data
 * @param {string} data.path - Request path/URL
 * @param {string} data.method - HTTP method (GET, POST, etc.)
 * @param {number} data.status - HTTP status code
 * @param {number} data.bytes - Response size in bytes (optional)
 * @param {number} data.duration - Response time in microseconds (optional)
 * @param {boolean} useBeacon - Use sendBeacon for page unload (optional)
 */
function sendLog(data, useBeacon = true) {
    if (!ENABLE_LOGGING) {
        //console.log('AccessLogger: Logging disabled');
        return;
    }
    
    // Exclude AccessLogger.php requests from being logged
    if (data.path && data.path.includes('AccessLogger.php')) {
        return;
    }

    //console.log('AccessLogger: Sending log ->', data, 'useBeacon:', useBeacon, 'Endpoint:', LOG_ENDPOINT);

    try {
        if (useBeacon && navigator.sendBeacon) {
            // For sendBeacon to work without CORS preflight, use text/plain
            // Create a Blob with text/plain content type (avoids CORS preflight)
            const blob = new Blob([JSON.stringify(data)], { type: 'text/plain' });
            const result = navigator.sendBeacon(LOG_ENDPOINT, blob);
            //console.log('AccessLogger: sendBeacon result:', result);
        } else {
            // Use fetch with keepalive - visible in network tab but more reliable
            //console.log('AccessLogger: Using fetch API');
            fetch(LOG_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
                keepalive: true,
                credentials: 'omit' // Don't send credentials to avoid CORS issues
            }).then((response) => {
                console.log('AccessLogger: Fetch success', response.status);
            }).catch((error) => {
                console.error('AccessLogger: Fetch error', error);
            });
        }
    } catch (error) {
        // Silently fail to avoid breaking the app
        console.error('AccessLogger: Failed to send log', error);
    }
}

/**
 * Log route navigation
 * 
 * @param {string} path - Route path
 * @param {string} search - Query string (optional)
 */
export function logRoute(path, search = '') {
    // Use the full current pathname which already includes the basename
    const fullPath = window.location.pathname + search;
    
    //console.log('AccessLogger: Logging route ->', fullPath);
    
    sendLog({
        path: fullPath,
        method: 'GET',
        status: 200
    });
}

/**
 * Log API request
 * 
 * @param {string} url - API URL
 * @param {string} method - HTTP method
 * @param {number} status - HTTP status code
 * @param {number} bytes - Response size (optional)
 * @param {number} duration - Response time in microseconds (optional)
 */
export function logAPIRequest(url, method = 'GET', status = 200, bytes = null, duration = null) {
    // Exclude backend API requests
    if (shouldExcludeUrl(url)) {
        return;
    }
    
    const data = {
        path: getCarrierAwarePath(url), // Use carrier-aware path transformation
        method,
        status
    };
    
    if (bytes !== null) {
        data.bytes = bytes;
    }
    
    if (duration !== null) {
        data.duration = duration;
    }
    
    sendLog(data);
}

/**
 * Intercept and log fetch requests globally
 * Wraps native fetch to automatically log all requests
 */
export function interceptFetch() {
    if (!ENABLE_LOGGING) return;

    const originalFetch = window.fetch;
    
    window.fetch = async function(...args) {
        const startTime = performance.now();
        const url = typeof args[0] === 'string' ? args[0] : args[0].url;
        const method = args[1]?.method || 'GET';
        
        // Exclude backend API requests from logging
        if (shouldExcludeUrl(url)) {
            return originalFetch.apply(this, args);
        }
        
        try {
            const response = await originalFetch.apply(this, args);
            const endTime = performance.now();
            const duration = Math.round(endTime - startTime);
            
            // Get response size if available
            const contentLength = response.headers.get('content-length');
            const bytes = contentLength ? parseInt(contentLength) : null;
            
            // Log the request
            logAPIRequest(url, method, response.status, bytes);
            
            return response;
        } catch (error) {
            // Log failed request
            logAPIRequest(url, method, 0, null);
            throw error;
        }
    };
}

/**
 * Intercept and log XMLHttpRequest
 * Wraps XHR to automatically log all requests
 */
export function interceptXHR() {
    if (!ENABLE_LOGGING) return;

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
        this._logMethod = method;
        this._logUrl = url;
        this._shouldExclude = shouldExcludeUrl(url);
        return originalOpen.apply(this, [method, url, ...args]);
    };
    
    XMLHttpRequest.prototype.send = function(...args) {
        // Exclude backend API requests from logging
        if (!this._shouldExclude) {
            this.addEventListener('load', function() {
                const contentLength = this.getResponseHeader('content-length');
                const bytes = contentLength ? parseInt(contentLength) : null;
                
                logAPIRequest(this._logUrl, this._logMethod, this.status, bytes);
            });
            
            this.addEventListener('error', function() {
                logAPIRequest(this._logUrl, this._logMethod, 0, null);
            });
        }
        
        return originalSend.apply(this, args);
    };
}

/**
 * Get the carrier basename from current URL
 * Mirrors the getBasename() logic from App.js
 * Handles both /carrier/route and /route patterns
 * @returns {string} Carrier basename (e.g., '/cigna') or '/'
 */
function getCarrierBasename() {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean); // Remove empty strings
    
    if (segments.length === 0) return '/';
    
    const firstSegment = segments[0].toLowerCase();
    const EXCLUDED_ROUTES = ['logout', 'slpmember', 'login', 'ssoauthcallback', 'ssoexternal'];
    
    // If first segment is excluded but we have more segments, check if it's a carrier/excluded pattern
    // Example: /cigna/login should extract 'cigna', but /login should return '/'
    if (EXCLUDED_ROUTES.includes(firstSegment)) {
        return '/';
    }
    
    // Check if second segment is excluded (carrier/login pattern)
    if (segments.length > 1 && EXCLUDED_ROUTES.includes(segments[1].toLowerCase())) {
        // First segment is the carrier
        return `/${segments[0]}`;
    }
    
    // Normal case: first segment is the carrier
    return `/${segments[0]}`;
}

/**
 * Transform resource URL to include carrier prefix for static files
 * @param {string} resourceUrl - Full resource URL
 * @returns {string} Path with carrier prefix if applicable
 */
function getResourcePath(resourceUrl) {
    try {
        const urlObj = new URL(resourceUrl);
        const pathname = urlObj.pathname;
        
        // If it's a same-origin resource, prepend carrier
        if (urlObj.origin === window.location.origin) {
            const carrier = getCarrierBasename();
            
            // If we have a valid carrier and path doesn't already start with it
            if (carrier !== '/' && !pathname.startsWith(carrier)) {
                return carrier + pathname;
            }
        }
        
        // For all other cases, return the original pathname
        return pathname;
    } catch (e) {
        // If URL parsing fails, return as-is
        return resourceUrl;
    }
}

/**
 * Get path with carrier prefix for logging
 * Handles both same-origin and external URLs
 * @param {string} url - URL to transform
 * @returns {string} Path or full URL with carrier context
 */
function getCarrierAwarePath(url) {
    try {
        const urlObj = new URL(url);
        
        // If same origin, just return the carrier-prefixed path
        if (urlObj.origin === window.location.origin) {
            return getResourcePath(url);
        }
        
        // For external URLs, return full URL (backend will handle it differently)
        return url;
    } catch (e) {
        // If URL parsing fails, treat as relative path and prepend carrier
        const carrier = getCarrierBasename();
        if (carrier !== '/' && !url.startsWith(carrier)) {
            return carrier + url;
        }
        return url;
    }
}

/**
 * Log static resource loads (CSS, JS, images, fonts)
 * Uses Performance API to track resource loads from this server
 * Excludes external API calls
 */
export function logResourceLoads() {
    if (!ENABLE_LOGGING) return;

    // Log already loaded resources
    const resources = performance.getEntriesByType('resource');
    resources.forEach(resource => {
        // Skip external API calls and AccessLogger.php
        if (shouldExcludeUrl(resource.name)) {
            return;
        }
        
        const status = resource.responseStatus || 200;
        const bytes = resource.transferSize || resource.encodedBodySize || null;
        // Convert duration from milliseconds to microseconds
        const duration = resource.duration ? Math.round(resource.duration * 1000) : null;
        
        const logData = {
            path: getResourcePath(resource.name), // Use carrier-aware path
            method: 'GET',
            status
        };
        
        if (bytes !== null) {
            logData.bytes = bytes;
        }
        
        if (duration !== null) {
            logData.duration = duration;
        }
        
        sendLog(logData);
    });
    
    // Monitor new resource loads
    const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
            if (entry.entryType === 'resource') {
                // Skip external API calls and AccessLogger.php
                if (shouldExcludeUrl(entry.name)) {
                    return;
                }
                
                const status = entry.responseStatus || 200;
                const bytes = entry.transferSize || entry.encodedBodySize || null;
                // Convert duration from milliseconds to microseconds
                const duration = entry.duration ? Math.round(entry.duration * 1000) : null;
                
                const logData = {
                    path: getResourcePath(entry.name), // Use carrier-aware path
                    method: 'GET',
                    status
                };
                
                if (bytes !== null) {
                    logData.bytes = bytes;
                }
                
                if (duration !== null) {
                    logData.duration = duration;
                }
                
                sendLog(logData);
            }
        });
    });
    
    observer.observe({ entryTypes: ['resource'] });
    
    return observer;
}

/**
 * React Hook: Track route changes
 * Use this in your App component
 * 
 * @example
 * function App() {
 *   useRouteLogger();
 *   return <Router>...</Router>
 * }
 */
export function useRouteLogger() {
    const location = useLocation();
    
    useEffect(() => {
        logRoute(location.pathname, location.search);
    }, [location]);
}

/**
 * Initialize all logging mechanisms
 * Call this once at app startup
 * 
 * @param {Object} options Configuration options
 * @param {boolean} options.interceptFetch - Intercept fetch API (default: false - no API tracking)
 * @param {boolean} options.interceptXHR - Intercept XMLHttpRequest (default: false - no API tracking)
 * @param {boolean} options.logResources - Log static resources (default: false - routes only)
 * @returns {Object} Cleanup function and observer references
 */
export function initializeAccessLogger(options = {}) {
    if (!ENABLE_LOGGING) {
        //console.log('AccessLogger: Disabled via environment variable');
        return { cleanup: () => {} };
    }
    
    const {
        interceptFetch: shouldInterceptFetch = false,
        interceptXHR: shouldInterceptXHR = false,
        logResources: shouldLogResources = false
    } = options;
    
    let resourceObserver = null;
    
    // Intercept fetch
    if (shouldInterceptFetch) {
        interceptFetch();
    }
    
    // Intercept XHR
    if (shouldInterceptXHR) {
        interceptXHR();
    }
    
    // Log resources
    if (shouldLogResources) {
        resourceObserver = logResourceLoads();
    }
    
    //console.log('AccessLogger: Initialized (routes and static resources only)');
    
    // Return cleanup function
    return {
        cleanup: () => {
            if (resourceObserver) {
                resourceObserver.disconnect();
            }
        },
        observer: resourceObserver
    };
}

/**
 * Batch logger for multiple requests
 * Useful for logging multiple events at once
 */
export class BatchLogger {
    constructor(flushInterval = 5000) {
        this.queue = [];
        this.flushInterval = flushInterval;
        this.timer = null;
        this.startTimer();
    }
    
    add(data) {
        this.queue.push(data);
        
        // Auto-flush if queue gets too large
        if (this.queue.length >= 10) {
            this.flush();
        }
    }
    
    async flush() {
        if (this.queue.length === 0) return;
        
        const batch = [...this.queue];
        this.queue = [];
        
        // Send all logs
        await Promise.all(batch.map(data => sendLog(data)));
    }
    
    startTimer() {
        this.timer = setInterval(() => {
            this.flush();
        }, this.flushInterval);
    }
    
    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.flush(); // Flush remaining logs
        }
    }
}

// Export default object with all functions
export default {
    logRoute,
    logAPIRequest,
    interceptFetch,
    interceptXHR,
    logResourceLoads,
    useRouteLogger,
    initializeAccessLogger,
    BatchLogger
};